import {Component, Input, OnChanges, SimpleChanges, TemplateRef, ViewChild} from '@angular/core';
import {take} from 'rxjs/operators';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {ToastrService} from 'ngx-toastr';
import {ChampFieldType, DonneesChampModel} from '../parametre-donnees.types';
import {ParametreDonneesStateService} from '../parametre-donnees-state.service';
import {newRandomId, slugFromLibelle} from '../parametre-donnees-helpers';

@Component({
  selector: 'app-parametre-donnees-champs-tab',
  templateUrl: './parametre-donnees-champs-tab.component.html',
  styleUrls: ['./parametre-donnees-champs-tab.component.scss']
})
export class ParametreDonneesChampsTabComponent implements OnChanges {
  @Input() moduleKey = 'produits';

  @ViewChild('champModalTpl') champModalTpl!: TemplateRef<unknown>;
  @ViewChild('usagesModalTpl') usagesModalTpl!: TemplateRef<unknown>;

  rows: DonneesChampModel[] = [];
  editing: DonneesChampModel | null = null;
  isNew = false;
  codeTouchedManually = false;
  codeError = '';

  usagesField: DonneesChampModel | null = null;

  readonly typeOptions: { value: ChampFieldType; label: string }[] = [
    { value: 'texte', label: 'Texte' },
    { value: 'texte_long', label: 'Texte long' },
    { value: 'nombre', label: 'Nombre' },
    { value: 'devise', label: 'Devise' },
    { value: 'pourcentage', label: 'Pourcentage' },
    { value: 'date', label: 'Date' },
    { value: 'liste', label: 'Liste' },
    { value: 'relation', label: 'Relation' },
    { value: 'bool', label: 'Booléen' },
    { value: 'email', label: 'E-mail' }
  ];

  readonly modulesRelation = ['Produits', 'Clients', 'Fournisseurs', 'Stock', 'Commandes'];

  private modalRef: { close: () => void; dismiss: () => void } | null = null;

  constructor(
    private readonly modal: NgbModal,
    private readonly toastr: ToastrService,
    private readonly state: ParametreDonneesStateService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['moduleKey']) {
      this.reload();
    }
  }

  reload(): void {
    this.state.syncChamps(this.moduleKey).subscribe({
      next: () => {
        this.rows = this.state.getChamps(this.moduleKey).sort((a, b) => a.ordre - b.ordre);
      },
      error: () => {
        this.rows = this.state.getChamps(this.moduleKey).sort((a, b) => a.ordre - b.ordre);
      }
    });
  }

  trackById(_: number, row: DonneesChampModel): string {
    return row.id;
  }

  openAdd(): void {
    this.isNew = true;
    this.codeTouchedManually = false;
    this.codeError = '';
    this.editing = this.createEmptyChamp();
    this.modalRef = this.modal.open(this.champModalTpl, {
      size: 'lg',
      scrollable: true,
      backdrop: 'static',
      windowClass: 'parametre-donnees-champ-modal-window'
    }) as { close: () => void; dismiss: () => void };
  }

  openEdit(row: DonneesChampModel): void {
    this.isNew = false;
    this.codeTouchedManually = true;
    this.codeError = '';
    this.editing = this.cloneChamp(row);
    this.modalRef = this.modal.open(this.champModalTpl, {
      size: 'lg',
      scrollable: true,
      backdrop: 'static',
      windowClass: 'parametre-donnees-champ-modal-window'
    }) as { close: () => void; dismiss: () => void };

    const ptid = Number(row.id);
    if (row.type === 'liste' && Number.isFinite(ptid) && ptid > 0) {
      this.state
        .getSelectParamsForParamTable(ptid)
        .pipe(take(1))
        .subscribe({
          next: (serverOpts) => {
            if (!this.editing || this.editing.id !== row.id || serverOpts.length === 0) {
              return;
            }
            this.editing.liste = this.editing.liste ?? { monoOuMultiple: 'mono', valeurs: [] };
            this.editing.liste.valeurs = serverOpts.map((o) => ({
              label: o.libelle,
              value: o.value || String(o.id),
              couleur: o.couleur,
              selectParamId: o.id
            }));
          },
          error: () => {}
        });
    }
  }

  duplicate(row: DonneesChampModel): void {
    const copy = this.cloneChamp(row);
    copy.id = newRandomId('champ');
    copy.code = `${row.code}_copie`;
    copy.libelle = `${row.libelle} (copie)`;
    copy.ordre = this.nextOrdre();
    this.state.saveChampApi(this.moduleKey, copy, true).subscribe({
      next: () => {
        this.reload();
        this.toastr.success('Champ dupliqué.', 'Succès');
      },
      error: () => {}
    });
  }

  toggleActif(row: DonneesChampModel): void {
    this.state.patchChampActifApi(this.moduleKey, row, !row.actif).subscribe({
      next: () => this.reload(),
      error: () => {}
    });
  }

  delete(row: DonneesChampModel): void {
    if (!confirm(`Supprimer le champ « ${row.libelle} » ?`)) {
      return;
    }
    this.state.deleteChampApi(this.moduleKey, row.id).subscribe({
      next: () => {
        this.reload();
        this.toastr.success('Champ supprimé.', 'Succès');
      },
      error: () => {}
    });
  }

  openUsages(row: DonneesChampModel): void {
    this.usagesField = row;
    this.modal.open(this.usagesModalTpl, { centered: true });
  }

  onLibelleInput(): void {
    if (!this.editing) {
      return;
    }
    if (!this.isNew || this.codeTouchedManually) {
      return;
    }
    this.editing.code = slugFromLibelle(this.editing.libelle);
  }

  onCodeInput(): void {
    this.codeTouchedManually = true;
  }

  saveChamp(): void {
    if (!this.editing) {
      return;
    }
    const code = (this.editing.code || '').trim().toLowerCase();
    if (!code) {
      this.codeError = 'Le code technique est obligatoire.';
      return;
    }
    const dup = this.rows.some((r) => {
      if (r.code.toLowerCase() !== code) {
        return false;
      }
      if (this.isNew) {
        return true;
      }
      return r.id !== this.editing!.id;
    });
    if (dup) {
      this.codeError = 'Ce code est déjà utilisé pour un autre champ.';
      return;
    }
    this.codeError = '';

    const normalized = this.normalizeBeforeSave(this.editing);
    this.state.saveChampApi(this.moduleKey, normalized, this.isNew).subscribe({
      next: () => {
        this.reload();
        this.modalRef?.close();
        this.modalRef = null;
        this.toastr.success('Champ enregistré.', 'Succès');
      },
      error: () => {}
    });
  }

  cancelChamp(): void {
    this.modalRef?.dismiss();
    this.modalRef = null;
  }

  onTypeChange(): void {
    if (!this.editing) {
      return;
    }
    const t = this.editing.type;
    if (t === 'liste' && !this.editing.liste) {
      this.editing.liste = { monoOuMultiple: 'mono', valeurs: [{ label: 'Option 1', value: 'opt1' }] };
    }
    if (t === 'relation') {
      this.ensureRelationBloc();
    }
    if (t === 'nombre' || t === 'devise' || t === 'pourcentage') {
      this.ensureNombreBloc();
    }
  }

  addListeValeur(): void {
    if (!this.editing) {
      return;
    }
    if (!this.editing.liste) {
      this.editing.liste = { monoOuMultiple: 'mono', valeurs: [] };
    }
    this.editing.liste.valeurs.push({ label: '', value: '' });
  }

  removeListeValeur(i: number): void {
    if (!this.editing?.liste?.valeurs) {
      return;
    }
    this.editing.liste.valeurs.splice(i, 1);
  }

  ensureNombreBloc(): void {
    if (!this.editing) {
      return;
    }
    if (!this.editing.nombre) {
      this.editing.nombre = { precision: 2, minimum: null, maximum: null };
    }
  }

  ensureRelationBloc(): void {
    if (!this.editing) {
      return;
    }
    if (!this.editing.relation) {
      this.editing.relation = { moduleCible: 'Produits', typeLiaison: '1-n' };
    }
  }

  showBlocListe(): boolean {
    return this.editing?.type === 'liste';
  }

  showBlocRelation(): boolean {
    return this.editing?.type === 'relation';
  }

  showBlocNombre(): boolean {
    const t = this.editing?.type;
    return t === 'nombre' || t === 'devise' || t === 'pourcentage';
  }

  private normalizeBeforeSave(m: DonneesChampModel): DonneesChampModel {
    const code = m.code.trim().toLowerCase();
    const libelle = m.libelle.trim();
    const base = this.cloneChamp(m);
    base.code = code;
    base.libelle = libelle;
    base.api = { ...base.api, nomTechniqueApi: base.api.nomTechniqueApi || code };
    if (base.type !== 'liste') {
      delete base.liste;
    } else if (base.liste) {
      const v = base.liste.valeurs.filter((x) => (x.label || '').trim() && (x.value || '').trim());
      base.liste = { ...base.liste, valeurs: v };
    }
    if (!this.showBlocRelation()) {
      delete base.relation;
    }
    if (!this.showBlocNombre()) {
      delete base.nombre;
    } else {
      base.nombre = base.nombre ?? { precision: 2, minimum: null, maximum: null };
    }
    return base;
  }

  private nextOrdre(): number {
    if (this.rows.length === 0) {
      return 1;
    }
    return Math.max(...this.rows.map((r) => r.ordre)) + 1;
  }

  private createEmptyChamp(): DonneesChampModel {
    const ordre = this.nextOrdre();
    const id = newRandomId('champ');
    return {
      id,
      ordre,
      code: '',
      libelle: '',
      description: '',
      type: 'texte',
      groupe: 'Général',
      placeholder: '',
      valeurParDefaut: '',
      obligatoire: false,
      actif: true,
      unique: false,
      lectureSeule: false,
      utiliseDans: '—',
      affichage: {
        afficherFiche: true,
        afficherListe: true,
        afficherFiltre: false,
        afficherRechercheAvancee: false,
        afficherImpression: true,
        afficherPos: false,
        afficherMobile: true,
        ongletCible: 'principal',
        sectionCible: 'general',
        ordreAffichage: ordre,
        largeurColonne: 'auto'
      },
      api: {
        exposeApi: true,
        nomTechniqueApi: '',
        lectureSeuleApi: false,
        autoriseCreation: true,
        autoriseMiseAJour: true,
        visibleWebhook: false
      },
      utilisation: {
        promoDeclenchement: false,
        promoExclusion: false,
        promoRegroupement: false,
        tarifRegles: false,
        tarifGenerationPrix: false,
        reportingBi: true,
        reportingExports: true,
        workflowValidations: false,
        workflowAutomatisations: false,
        rechercheSimple: true,
        rechercheAvancee: true
      }
    };
  }

  private cloneChamp(m: DonneesChampModel): DonneesChampModel {
    return JSON.parse(JSON.stringify(m)) as DonneesChampModel;
  }

}
